import React from 'react';
import { useTranslation } from 'react-i18next';
import { useFormikContext } from 'formik';
import { useAppDispatch, useAppSelector } from '../app/hooks';
import { OrderingOperations, OrderingSelectors, OrderingUtils } from 'polygon-ordering';
import RedcatPaySavedCardPaymentForm from '../shared/redcatpayments/components/RedcatPaySavedCardPaymentForm';
import { enqueueErrorSnackbar, enqueueSuccessSnackbar } from '../utils/snackbar';
import { getRedcatPaySaleFailureMessage } from '../utils/errors';
import history from '../history';
import { MEMBER_MONEY_FIELDS_ROUTE } from '../constants/loyaltyRoutes';

const { getMember } = OrderingSelectors;
const { dollarsToCents } = OrderingUtils;
const { finaliseLoyaltyOrderMember, fetchMember } = OrderingOperations;

interface MemberMoneyFormValues {
  Amount: string;
}

const MemberMoneyRedcatPayTopUp = () => {
  const { t } = useTranslation();
  const { values, resetForm, validateForm } = useFormikContext<MemberMoneyFormValues>();
  const dispatch = useAppDispatch();
  const member = useAppSelector(getMember);

  const amount = dollarsToCents(parseFloat(values.Amount) || 0);

  const loyalty = {
    loyalty_purchase_type: 'member_money_topup',
  };

  const finaliseTopUp = async (reference: string) => {
    try {
      await dispatch(finaliseLoyaltyOrderMember(reference)).unwrap();
      await dispatch(fetchMember({ authenticationMethod: 'member' }));
      enqueueSuccessSnackbar('Top-up successful');
      resetForm();
      // resetForm() clears state.errors directly without re-running validation, so
      // isValid (derived from state.errors) would otherwise stay true against the
      // now-empty Amount field until the next change/blur/submit. validateForm()
      // without args reads from a stale closure over state.values (the just-paid
      // amount, still valid) because it's called synchronously before the reset's
      // state update lands, so we pass the reset-to values explicitly instead.
      await validateForm({ Amount: '' });
      history.push(MEMBER_MONEY_FIELDS_ROUTE);
    } catch (err: any) {
      const failureMessage = getRedcatPaySaleFailureMessage(err, {
        refundFailedMessage: t('refundFailedMessage'),
        fallbackMessage: t('memberMoneyTopUpFailedMessage'),
      });
      enqueueErrorSnackbar(failureMessage);
    }
  };

  return (
    <RedcatPaySavedCardPaymentForm
      amount={amount}
      loyalty={loyalty}
      receiptEmail={member?.email ?? undefined}
      buttonLabel={t('button.topupButton')}
      failureMessage={t('memberMoneyTopUpFailedMessage')}
      goBackOnNewCardError
      onFinalise={finaliseTopUp}
    />
  );
};

export default MemberMoneyRedcatPayTopUp;
